<template>
  <div id="content" ref="content" style="background: transparent"></div>
</template>

<script>
export default {
  name: "MathRenderPage",

  data() {
    return {
      params: {},
    };
  },

  created() {
    const p = new URLSearchParams(window.location.search);
    this.params = {
      content: (p.get("content") || "").replace(/\\\$/g, "$"),
      theme: p.get("theme") || "light",
      dir: p.get("dir") || "rtl",
      scale: parseFloat(p.get("scale") || "1.0"),
    };
  },

  mounted() {
    this._applyTheme();
    this._injectMathJax();
    this._loadContent();
  },

  beforeUnmount() {
    this._cleanupStyles();
  },

  methods: {
    _applyTheme() {
      const { theme, dir, scale } = this.params;
      const isDark = theme === "dark";
      const textColor = isDark ? "#FFFFFF" : "#000000";
      const fontSize = (20 * scale).toFixed(2) + "px";

      document.documentElement.classList.add("math-render-page");
      document.documentElement.setAttribute("dir", dir);
      document.documentElement.setAttribute(
        "lang",
        dir === "rtl" ? "ar" : "en"
      );

      const style = document.createElement("style");
      style.id = "math-render-theme";
      style.textContent = [
        "html.math-render-page, html.math-render-page body {",
        "  margin: 0;",
        "  padding: 0;",
        "  background: transparent !important;",
        "  background-color: transparent !important;",
        "  font-family: 'Amiri', 'Scheherazade New', 'Traditional Arabic', 'Arial Unicode MS', serif;",
        "  overflow: visible !important;",
        "  height: auto !important;",
        "  max-height: none !important;",
        "  -webkit-overflow-scrolling: touch;",
        "  color: " + textColor + ";",
        "  font-size: " + fontSize + ";",
        "  line-height: 1.9;",
        "  word-spacing: 0.12em;",
        "  direction: " + dir + ";",
        "  text-align: " + (dir === "rtl" ? "right" : "left") + ";",
        "}",
        "html.math-render-page body {",
        "  padding: 2px 4px;",
        "  box-sizing: border-box;",
        "  overflow-wrap: anywhere;",
        "  word-break: break-word;",
        "}",
        "html.math-render-page p {",
        "  margin: 0 0 0.5em 0;",
        "}",
        "html.math-render-page img,",
        "html.math-render-page video,",
        "html.math-render-page iframe {",
        "  max-width: 100%;",
        "  height: auto;",
        "}",
        "html.math-render-page .MathJax,",
        "html.math-render-page .MathJax_Display {",
        "  overflow-x: auto;",
        "  overflow-y: hidden;",
        "  -webkit-overflow-scrolling: touch;",
        "  color: " + textColor + " !important;",
        "  outline: none;",
        "}",
        "html.math-render-page .MathJax_Display {",
        "  margin: 0.4em 0 !important;",
        "  text-align: " + (dir === "rtl" ? "right" : "left") + " !important;",
        "}",
        "html.math-render-page #content {",
        "  margin: 0 !important;",
        "  padding: 0 !important;",
        "}",
        "html.math-render-page #app {",
        "  background: transparent !important;",
        "  background-color: transparent !important;",
        "}",
        "html.math-render-page ::-webkit-scrollbar { width: 6px; }",
        "html.math-render-page ::-webkit-scrollbar-thumb {",
        "  border-radius: 6px;",
        "  background-color: " + textColor + ";",
        "}",
      ].join("\n");

      document.head.appendChild(style);

      if (!document.getElementById("amiri-font")) {
        const link = document.createElement("link");
        link.id = "amiri-font";
        link.rel = "stylesheet";
        link.href = "https://fonts.googleapis.com/css?family=Amiri";
        document.head.appendChild(link);
      }
    },

    _injectMathJax() {
      if (window.MathJax) {
        return;
      }

      const config = document.createElement("script");
      config.type = "text/x-mathjax-config";
      config.textContent = [
        'MathJax.Ajax.config.path["arabic"] = "https://cdn.rawgit.com/OmarIthawi/arabic-mathjax/v1.2/dist";',
        "MathJax.Hub.Config({",
        '  extensions: ["[arabic]/arabic.js"],',
        '  jax: ["input/TeX", "output/HTML-CSS"],',
        "  Arabic: {",
        "    numbersMap: {",
        "      '0':'0','1':'1','2':'2','3':'3','4':'4',",
        "      '5':'5','6':'6','7':'7','8':'8','9':'9'",
        "    }",
        "  },",
        "  tex2jax: {",
        "    inlineMath:  [['$','$'],['\\\\(','\\\\)']],",
        "    displayMath: [['$$','$$'],['\\\\[','\\\\]']],",
        "    processEscapes: true",
        "  },",
        '  "HTML-CSS": {',
        '    undefinedFamily: "Amiri, serif",',
        "    availableFonts: [],",
        "    preferredFont:  null,",
        "    webFont:        null,",
        "    scale: 100,",
        "    linebreaks: { automatic: true }",
        "  },",
        "  showProcessingMessages: false,",
        '  messageStyle: "none"',
        "});",
      ].join("\n");
      document.head.appendChild(config);

      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.7/MathJax.js?config=TeX-AMS_HTML";
      script.async = true;
      script.onload = () => {
        window.MathJax.Hub.Register.StartupHook("End", () => {
          window.MathJax.Hub.Queue([
            "Typeset",
            window.MathJax.Hub,
            document.body,
          ]);
          window.MathJax.Hub.Queue(() => {
            setTimeout(this._sendHeight, 50);
          });
        });
      };
      document.head.appendChild(script);
    },

    _loadContent() {
      const el = this.$refs.content;
      const html = this.params.content;

      if (!html) {
        el.innerText = "No content provided.";
        this._sendHeight();
        return;
      }

      el.innerHTML = html;

      if (window.MathJax) {
        window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, el]);
        window.MathJax.Hub.Queue(() => {
          setTimeout(this._sendHeight, 100);
        });
      } else {
        setTimeout(this._sendHeight, 300);
      }
    },

    _sendHeight() {
      try {
        var el = this.$refs.content;
        var height = Math.ceil(
          el ? el.offsetHeight : document.body.offsetHeight
        ).toString();
        if (
          window.webkit &&
          window.webkit.messageHandlers &&
          window.webkit.messageHandlers.HeightChannel
        ) {
          window.webkit.messageHandlers.HeightChannel.postMessage(height);
        } else if (window.HeightChannel) {
          window.HeightChannel.postMessage(height);
        }
      } catch (e) {
        // silent
      }
    },

    _cleanupStyles() {
      const style = document.getElementById("math-render-theme");
      if (style) style.remove();
      document.documentElement.classList.remove("math-render-page");
      document.documentElement.removeAttribute("dir");
      document.documentElement.removeAttribute("lang");
    },
  },
};
</script>
